// Items list for combo (cx / scx). Unlike the sentiments / tags one next door this renders the
// drill-down level of a real question, so it keeps every branch the shared ItemsListView
// calculator carried: the format flags, thumbnails, skip-to targets and the trait breakdown.
// Combo is never a pre-qualification question, so no screener marks or blanks a row. The pieces
// it shares with that sibling come from the open-ended lib.
//
// Handlers arrive as callbacks, the way `onSort` always did, so nothing here depends on a `this`.

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { isClickable, processText } from '@aytm/app/stats/page/components/lib/utils'
import { getTraitValues } from '@aytm/app/stats/page/question-views/sh/components/chart/lib/traitValues'
import {
  PALETTE_SIZE,
  answerSettled,
  applySorted,
  buildTraitTable,
  byCellValue,
  byPosition,
  hasBaseIn,
  progressTitle,
  valueCell
} from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

// An OE leaf has no variants to list, so its corpus stands in — the legacy list swapped to the
// popular-words view here. Raw payload text is encoded at rest, so it is title-safe as-is.
const calcLeafWordsTableProps = answer => {
  const words = 'loading' == answer ? null : answer?.cloudWords?.words
  if (!words?.length) return { cols: [], rows: [] }

  return {
    cols: [
      { type: 'title', title: 'Most popular words' },
      { type: 'value', title: 'Frequency' }
    ],
    rows: [...words]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map((word, index) => ({
        cells: [{ title: word[0] }, { value: word[1] }],
        position: index + 1
      }))
  }
}

export const calcTableProps = ({
  id,
  answer,
  answerVariants = [],
  answerType = 'amount',
  type,
  format,
  components,
  percentDecimals,
  rType = 'cases',
  sortOrder,
  trait = null,
  traitName = null,
  isTraitType = false,
  commentsEnabled = true,
  clickable = true,
  pointPath = null,
  onSort,
  onLabelClick,
  onCommentsClick,
  onSkipsToClick
}) => {
  const variants = (answerVariants || []).filter(av => av)
  if (!variants.length) return calcLeafWordsTableProps(answer)

  const isLoading = 'loading' == answer
  const settled = answerSettled(answer)
  const values = isLoading || undefined === answer || null === answer ? {} : answer

  const hideTotal = true === format?.hideTotal
  const ignoreRefs = true === format?.ignoreRefs
  const ignoreTraits = true === format?.ignoreTraits
  const ignoreAmount = true === format?.percentsOnly
  const ignorePercents = true === format?.amountOnly
  const textAmount = true === format?.textAmount

  const withTrait = Boolean(trait) && isTraitType && !ignoreTraits
  const traitValues = getTraitValues(trait)

  const commentsOn =
    commentsEnabled && (!components || undefined === components.comments || true === components.comments)

  const cols = [
    { type: 'title', title: 'Answers', sort: byPosition },
    {
      type: 'progress',
      title: progressTitle({
        answer: values,
        answerType,
        type,
        rType,
        hasBase: hasBaseIn(values),
        hideTotal
      }),
      percentDecimals,
      sort: byCellValue(1, 'percents'),
      format: value => (Number.isFinite(value) ? (textAmount ? value : formatAmounts(value)) : value)
    }
  ]

  const rows = variants.map(av => {
    const item = values[`a${av.position}`] || {}

    const titleCell = {
      title: processText(av.text),
      colorIndex: withTrait ? 0 : ((av.position - 1) % PALETTE_SIZE) + 1
    }
    if (!ignoreRefs) titleCell.label = `A${av.position}`

    if (Object.hasOwn(av, 'image')) titleCell.image = av.image

    if (Object.hasOwn(av, 'skipsTo')) {
      titleCell.skips_to = av.skipsTo
      titleCell.onSkipsToClick = onSkipsToClick
    }

    if (commentsOn && Object.hasOwn(item, 'comments')) {
      titleCell.comments = item.comments
      // the handlers live on the block, which no longer holds these rows -- hand them in.
      // The Comments page lists the combo's answers nested, so a drilled row is addressed by its
      // ancestors' positions ahead of its own (`2a4a3`); the handler writes that as the answer step.
      const drillPath = av.path?.length ? [...av.path, av.position].join('a') : null
      titleCell.onCommentsClick = drillPath
        ? event => onCommentsClick?.(event, drillPath, [{ position: drillPath }])
        : (event, position) => onCommentsClick?.(event, position, variants)
    }

    // cx labels drill, so the pointer mirrors the drill guard: a leaf with neither children nor
    // its own corpus is not drillable. scx labels filter and stay clickable on every row.
    if (isClickable(format, clickable) && ('cx' !== type || av.children || null != item.cloudWords)) {
      titleCell.onLabelClick = (event, position) => onLabelClick?.(event, position, variants)
      // the legacy list drilled on the answer text itself, so the name stays a drill target too
      if ('cx' === type) titleCell.onTextClick = titleCell.onLabelClick
    }

    const row = {
      cells: [
        titleCell,
        valueCell({
          item,
          answerType,
          rType,
          isLoading,
          settled,
          ignoreAmount,
          ignorePercents
        })
      ],
      position: av.position
    }

    if (pointPath) row.pointId = [...pointPath, av.position].join('.')

    if (withTrait) {
      const traitAnswer = item[traitName]
      if (undefined !== traitAnswer) {
        row.table = buildTraitTable({ id, trait, traitName, traitValues, traitAnswer })
      }
    }

    return row
  })

  const sortedCols = applySorted(cols, sortOrder)

  // the sort resolves the picked column out of the built set, which only exists here
  return { id, cols: sortedCols, rows, onSort: (colIndex, dir) => onSort?.(colIndex, dir, sortedCols) }
}
